import React, { useMemo } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartV2 from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import {
  useMetricByWeekIdV2,
  SplineChartV2DataPoint,
  SplineChartV2Series
} from 'src/components/BeaconV2Components/SplineChartV2';
import useMarketShareHooks from 'src/components/AtlasV2Components/Hooks/useMarketShareRequestHook';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useAppSelector, useMetricFormatter, useWeekRange, WeekRange } from 'src/utils/Hooks';
import { calculatePercentChange, safeDivide } from 'src/utils/app';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import convertSplineChartV2SeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/splineChartV2';
import saveAs from 'file-saver';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';

/**
 * Share of Total Traffic (by keyword) — shows entity traffic as a percentage of total market traffic.
 *
 * Uses Advanced Search for both entity and market data (same source as the rest of the Total Traffic
 * page). We fetch entity traffic with requestBuilder and market traffic with marketShareBuilder, then
 * compute share = entity / market per week and format as percent. The Doris path (AtlasV2DorisSplineChart
 * + showMarketShare) was tried but returns no data on this page, likely due to different API/entity context.
 *
 * Legend total metric (matches Doris share-of-traffic behavior):
 * - For YTD / 52w / 26w / etc.: period share = sum(entity clicks) / sum(market clicks) over the range (weighted average).
 * - For Last Week: last data point (share for the final week only).
 */

function computeShareSeries(
  entityData: SplineChartV2DataPoint[],
  marketData: SplineChartV2DataPoint[]
): SplineChartV2DataPoint[] {
  const marketByWeekId = new Map(marketData.map((d) => [d.weekId, d.value ?? 0]));
  return entityData.map((d) => ({
    weekId: d.weekId,
    value: safeDivide(d.value ?? 0, marketByWeekId.get(d.weekId) ?? 0)
  }));
}

/** Period share: sum(entity) / sum(market) over the range. Used for legend when not Last Week. */
function periodShare(
  entityData: SplineChartV2DataPoint[],
  marketData: SplineChartV2DataPoint[]
): number {
  const entitySum = entityData.reduce((s, d) => s + (d.value ?? 0), 0);
  const marketSum = marketData.reduce((s, d) => s + (d.value ?? 0), 0);
  return safeDivide(entitySum, marketSum);
}

/** Last data point (share for final week). Used for legend when Week Range is Last Week. */
function lastShareValue(shareData: SplineChartV2DataPoint[]): number {
  if (!shareData.length) return 0;
  const last = shareData[shareData.length - 1];
  return last?.value ?? 0;
}

const AtlasV2TotalTrafficShareByKeyword = () => {
  const formatMetric = useMetricFormatter();
  const weekRange = useWeekRange();
  const { requestBuilder, marketShareBuilder } = useMarketShareHooks();
  const primaryLegendDisplayName = useAppSelector((state) => state.mainTimePeriod.displayName);
  const comparisonLegendDisplayName = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const useLastValue = weekRange === WeekRange.LAST_WEEK;

  // Entity-scoped traffic (numerator)
  const entity = useMetricByWeekIdV2({
    indexName: 'traffic',
    fieldName: 'totalClicks',
    requestBuilder,
    metricTypeOverride: METRICTYPE.PERCENT
  });

  // Market-scoped traffic (denominator) — marketShareBuilder removes entity filter
  const market = useMetricByWeekIdV2({
    indexName: 'traffic',
    fieldName: 'totalClicks',
    requestBuilder: (builder) => marketShareBuilder(builder),
    metricTypeOverride: METRICTYPE.PERCENT
  });

  const isLoading = entity.isLoading || market.isLoading;

  const primaryShareData = useMemo(
    () => computeShareSeries(entity.primaryData, market.primaryData),
    [entity.primaryData, market.primaryData]
  );
  const comparisonShareData = useMemo(
    () => computeShareSeries(entity.comparisonData, market.comparisonData),
    [entity.comparisonData, market.comparisonData]
  );

  const primaryPeriodShare = useMemo(
    () => periodShare(entity.primaryData, market.primaryData),
    [entity.primaryData, market.primaryData]
  );

  // Restrict comparison to the same number of weeks as primary (e.g. YTD: first N weeks of prior year).
  // Otherwise comparison would sum over full comparison period (e.g. 52 weeks) and look inflated vs primary (e.g. 10 weeks).
  const comparisonDataForPeriod = useMemo(() => {
    const n = entity.primaryData.length;
    return {
      entity: entity.comparisonData.slice(0, n),
      market: market.comparisonData.slice(0, n)
    };
  }, [entity.primaryData.length, entity.comparisonData, market.comparisonData]);

  const comparisonPeriodShare = useMemo(
    () => periodShare(comparisonDataForPeriod.entity, comparisonDataForPeriod.market),
    [comparisonDataForPeriod]
  );

  const lastPrimaryShare = useMemo(() => lastShareValue(primaryShareData), [primaryShareData]);
  const lastComparisonShare = useMemo(() => lastShareValue(comparisonShareData), [comparisonShareData]);

  const primaryLegendValue = useLastValue ? lastPrimaryShare : primaryPeriodShare;
  const comparisonLegendValue = useLastValue ? lastComparisonShare : comparisonPeriodShare;

  const percentChange = useMemo(
    () =>
      comparisonLegendValue !== 0
        ? calculatePercentChange(primaryLegendValue, comparisonLegendValue)
        : undefined,
    [primaryLegendValue, comparisonLegendValue]
  );

  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: 'share-of-traffic-primary',
      name: primaryLegendDisplayName,
      data: primaryShareData
    }),
    [primaryShareData, primaryLegendDisplayName]
  );
  const comparisonSeries: SplineChartV2Series = useMemo(
    () => ({
      id: 'share-of-traffic-comparison',
      name: comparisonLegendDisplayName,
      data: comparisonShareData
    }),
    [comparisonShareData, comparisonLegendDisplayName]
  );

  const formattedPrimaryTotal = useMemo(
    () =>
      formatMetric(primaryLegendValue, METRICTYPE.PERCENT, { decimalPlaces: 2, showFullValue: true }) || '0.00%',
    [formatMetric, primaryLegendValue]
  );
  const formattedComparisonTotal = useMemo(
    () =>
      formatMetric(comparisonLegendValue, METRICTYPE.PERCENT, { decimalPlaces: 2, showFullValue: true }) || '0.00%',
    [formatMetric, comparisonLegendValue]
  );
  const formattedPercentChange = useMemo(
    () =>
      percentChange !== undefined
        ? formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
        : '--',
    [formatMetric, percentChange]
  );

  const hasNoData =
    !primaryShareData.length ||
    primaryShareData.every((d) => d.value === 0 || d.value == null);

  const convertSeriesToDelimitedData = () => {
    try {
      const data = convertSplineChartV2SeriesToDelimitedData(
        primarySeries,
        comparisonSeries,
        primaryLegendDisplayName,
        comparisonLegendDisplayName
      );
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, 'Share of Total Traffic.csv');
    } catch (e) {
      console.error('Export Share of Total Traffic CSV:', e);
    }
    return Promise.resolve();
  };

  return (
    <BeaconChartWithLegend
      title="Share of Total Traffic"
      showTitleSummary
      includeSummaryInfo
      primaryLegendProps={{
        displayName: primaryLegendDisplayName,
        metric: formattedPrimaryTotal,
        change: formattedPercentChange,
        direction: getDirection(percentChange)
      }}
      comparisonLegendProps={{
        displayName: comparisonLegendDisplayName,
        metric: formattedComparisonTotal,
        direction: getDirection(percentChange)
      }}
      loading={isLoading}
      loadingComponent={<SplineChartLoading />}
      convertSeriesToDelimitedData={convertSeriesToDelimitedData}
    >
      {hasNoData && !isLoading ? (
        <NoDataPlaceHolder
          title="Data currently unavailable"
          description="There is currently no data matching your selected criteria. Please try another query to view insights."
          flexProps={{ margin: '80px auto' }}
        />
      ) : (
        <SplineChartV2
          metricType={METRICTYPE.PERCENT}
          primarySeries={primarySeries}
          comparisonSeries={comparisonSeries}
        />
      )}
    </BeaconChartWithLegend>
  );
};

export default AtlasV2TotalTrafficShareByKeyword;
